import React, { memo, useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { areEqual } from 'Utils/equalityChecks';

import { SpinnerBlock } from 'Components/SpinnerBlock';
import { LoadMoreButton } from 'Components/Button';
import { EquipmentGantt } from 'Components/SerializedEquipment';
import { firstCompanyIdSelector } from 'Containers/Projects/selectors';

import { AssetHistoryModal } from '../AssetHistoryModal/AssetHistoryModal';
import { getSerializedEquipmentTimeline } from '../actions';
import { TimelineProjectRow, TimelineBar, EquipmentAsset } from '../types';

interface Props {
  // Bumped by the parent after a deploy/move/check-out so the timeline reloads from page 1.
  reloadToken?: number;
}

// Company-scoped serialized equipment Gantt (WEBAPP-FR-007). Fetches the dedicated timeline
// endpoint and renders the project-row Gantt. Rendered only inside the serialized workspace
// (behind `serializedEquipment`), so nothing here runs for a legacy company. Paginates by project
// (backend default 25/page): page 1 replaces, later pages append via Load More. Reloads from page 1
// whenever reloadToken changes.
function SerializedEquipmentTimelineContainer({ reloadToken = 0 }: Props) {
  const dispatch = useDispatch();
  const companyId = useSelector(firstCompanyIdSelector, areEqual);

  const [rows, setRows] = useState<TimelineProjectRow[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [fetching, setFetching] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failed, setFailed] = useState(false);
  // Asset whose Placement History is open (from clicking a bar) — the modal edits/deletes records.
  const [historyAsset, setHistoryAsset] = useState<EquipmentAsset | null>(null);
  // Guards against overlapping requests (double-clicking Load More) — state alone is stale within
  // the same tick.
  const loadingRef = useRef(false);

  const load = useCallback(
    async (page = 1) => {
      if (!companyId || loadingRef.current) {
        setFetching(false);
        return;
      }
      loadingRef.current = true;
      if (page > 1) setLoadingMore(true);
      else setFetching(true);
      setFailed(false);
      const response: any = await dispatch(getSerializedEquipmentTimeline(companyId, page));
      if (response?.data) {
        setRows((prev) => (page > 1 ? [...prev, ...response.data] : response.data));
        setCurrentPage(response?.meta?.current_page ?? page);
        setLastPage(response?.meta?.last_page ?? 1);
        setTotal(response?.meta?.total ?? response.data.length);
      } else if (page === 1) {
        setFailed(true);
      }
      setFetching(false);
      setLoadingMore(false);
      loadingRef.current = false;
    },
    [companyId, dispatch]
  );

  // Load on mount / company change, and reload from page 1 when the parent bumps reloadToken.
  useEffect(() => {
    load(1);
  }, [load, reloadToken]);

  const onLoadMore = useCallback(() => load(currentPage + 1), [load, currentPage]);

  // Clicking a bar opens that unit's Placement History (where the record can be edited/deleted).
  // The timeline bar carries only a lightweight asset; the History modal needs just id + name.
  const onBarClick = useCallback((bar: TimelineBar) => {
    setHistoryAsset({ id: bar.asset.id, name: bar.asset.name } as unknown as EquipmentAsset);
  }, []);

  // On close, reload from page 1 so any edited/deleted dates are reflected in the Gantt.
  const onCloseHistory = useCallback(() => {
    setHistoryAsset(null);
    load(1);
  }, [load]);

  if (fetching) {
    return <SpinnerBlock fetching />;
  }

  if (failed) {
    return (
      <div className="d-flex flex-column align-items-center p-4" role="alert" data-testid="timeline-error">
        <p className="text-muted mb-3">Couldn’t load the equipment timeline.</p>
        <button type="button" className="btn btn-outline-primary" onClick={() => load(1)}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <>
      <EquipmentGantt projects={rows} onBarClick={onBarClick} />
      {loadingMore && <SpinnerBlock fetching />}
      {!loadingMore && currentPage < lastPage && (
        <LoadMoreButton type="Projects" totalItems={total - rows.length} loadMoreItemsClick={onLoadMore} />
      )}
      <AssetHistoryModal isOpen={!!historyAsset} asset={historyAsset} onClose={onCloseHistory} />
    </>
  );
}

SerializedEquipmentTimelineContainer.defaultProps = {
  reloadToken: 0,
};

const SerializedEquipmentTimelineMemo = memo(SerializedEquipmentTimelineContainer, areEqual);

export { SerializedEquipmentTimelineMemo as SerializedEquipmentTimeline };
